import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import {
  GET_QUICKBOOKS_CONNECTION_STATUS,
  GET_QUICKBOOKS_AUTHORIZATION_URL,
  CONNECT_QUICKBOOKS,
  DISCONNECT_QUICKBOOKS,
} from '../../graphql/quickbooks';
import QuickBooksCustomerAssignments from './QuickBooksCustomerAssignments';

function QuickBooksApiSection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isConnecting, setIsConnecting] = useState(false);

  const { data, loading, error, refetch } = useQuery(GET_QUICKBOOKS_CONNECTION_STATUS, {
    fetchPolicy: 'network-only',
  });

  const [getAuthorizationUrl, { loading: authUrlLoading }] = useLazyQuery(GET_QUICKBOOKS_AUTHORIZATION_URL, {
    onCompleted: (data) => {
      if (data?.quickBooksAuthorizationUrl) {
        console.log('Redirecting to QuickBooks authorization URL:', data.quickBooksAuthorizationUrl);
        // Redirect to QuickBooks authorization page
        window.location.href = data.quickBooksAuthorizationUrl;
        setIsConnecting(true);
      } else {
        toast.error('Failed to get authorization URL: No URL returned');
        setIsConnecting(false);
      }
    },
    onError: (error) => {
      console.error('Error getting authorization URL:', error);
      toast.error(`Failed to get authorization URL: ${error.message}`);
      setIsConnecting(false);
    },
    fetchPolicy: 'network-only',
  });

  const generateStateToken = () => {
    // Generate a random state token for CSRF protection
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  const [disconnectQuickBooks, { loading: disconnectLoading }] = useMutation(DISCONNECT_QUICKBOOKS, {
    onCompleted: () => {
      toast.success('QuickBooks disconnected successfully');
      refetch();
    },
    onError: (error) => {
      toast.error(`Failed to disconnect: ${error.message}`);
    },
  });

  // Handle OAuth callback
  useEffect(() => {
    const quickbooksParam = searchParams.get('quickbooks');
    const messageParam = searchParams.get('message');

    if (quickbooksParam === 'connected') {
      toast.success('QuickBooks connected successfully!');
      // Clean up URL params
      setSearchParams({});
      refetch();
    } else if (quickbooksParam === 'error') {
      toast.error(`QuickBooks connection failed: ${messageParam || 'Unknown error'}`);
      // Clean up URL params
      setSearchParams({});
    }
  }, [searchParams, refetch, setSearchParams]);

  const handleConnect = () => {
    setIsConnecting(true);
    // Generate and store state token for CSRF protection
    const stateToken = generateStateToken();
    sessionStorage.setItem('quickbooks_oauth_state', stateToken);
    getAuthorizationUrl({
      variables: { state: stateToken }
    });
  };

  const handleDisconnect = async () => {
    if (window.confirm('Are you sure you want to disconnect QuickBooks? This will remove all stored tokens.')) {
      try {
        await disconnectQuickBooks();
      } catch (error) {
        console.error('Error disconnecting QuickBooks:', error);
      }
    }
  };

  const connectionStatus = data?.quickBooksConnectionStatus;
  const isConnected = connectionStatus?.isConnected || false;
  const expiresAt = connectionStatus?.expiresAt;
  const realmId = connectionStatus?.realmId;

  // Calculate days until expiration
  const getDaysUntilExpiration = () => {
    if (!expiresAt) return null;
    const expirationDate = new Date(expiresAt);
    const now = new Date();
    const diffTime = expirationDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilExpiration = getDaysUntilExpiration();
  const isExpiringSoon = daysUntilExpiration !== null && daysUntilExpiration <= 7;

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">QuickBooks Integration</h2>
      
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="ml-2">Checking connection status...</span>
        </div>
      ) : error ? (
        <div className="text-red-600 mb-4">
          Error loading connection status: {error.message}
        </div>
      ) : (
        <>
          {/* Connection Status */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-medium text-gray-900">Connection Status</h3>
                <p className="text-sm text-gray-500">
                  {isConnected
                    ? 'Your QuickBooks account is connected and ready to use'
                    : 'Connect your QuickBooks account to assign customers to properties'}
                </p>
              </div>
              <div className="flex items-center">
                <div className={`h-3 w-3 rounded-full mr-2 ${isConnected ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                <span className={`text-sm font-medium ${isConnected ? 'text-green-600' : 'text-gray-500'}`}>
                  {isConnected ? 'Connected' : 'Not Connected'}
                </span>
              </div>
            </div>

            {isConnected && (
              <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h4 className="text-sm font-medium text-green-800">QuickBooks Connected</h4>
                    <div className="mt-2 text-sm text-green-700">
                      {realmId && (
                        <p>Realm ID: <span className="font-mono">{realmId}</span></p>
                      )}
                      {expiresAt && (
                        <p className="mt-1">
                          Token expires: {new Date(expiresAt).toLocaleDateString()}
                          {daysUntilExpiration !== null && (
                            <span className={`ml-2 ${isExpiringSoon ? 'text-yellow-600 font-medium' : ''}`}>
                              ({daysUntilExpiration} {daysUntilExpiration === 1 ? 'day' : 'days'} remaining)
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!isConnected && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-yellow-800">QuickBooks Not Connected</h4>
                    <p className="mt-2 text-sm text-yellow-700">
                      Connect your QuickBooks account to enable customer assignment for properties.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-900">Account Management</h3>
                <p className="text-sm text-gray-500">
                  {isConnected
                    ? 'Disconnect your QuickBooks account if you need to switch accounts'
                    : 'Click Connect to authorize access to your QuickBooks account'}
                </p>
              </div>
              <div className="flex space-x-2">
                {!isConnected ? (
                  <button
                    onClick={handleConnect}
                    disabled={authUrlLoading || isConnecting}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
                  >
                    {authUrlLoading || isConnecting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Connecting...
                      </>
                    ) : (
                      'Connect QuickBooks'
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleDisconnect}
                    disabled={disconnectLoading}
                    className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md shadow-sm text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {disconnectLoading ? 'Disconnecting...' : 'Disconnect QuickBooks'}
                  </button>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {isConnected
                ? 'Disconnecting will remove all stored tokens. You will need to reconnect to use QuickBooks features.'
                : 'You will be redirected to QuickBooks to authorize access. After authorization, you will be redirected back to this page.'}
            </p>
          </div>

          {isConnected && <QuickBooksCustomerAssignments />}
        </>
      )}
    </div>
  );
}

export default QuickBooksApiSection;

